import React, { useState, useEffect } from "react";
import { Link, useParams } from "react-router-dom";
import SharedHelmet from "../Helmet";
import RelatedLinks from "../RelatedLinks";
import { FirstAds, SecondAds, MobileUnderCalAds } from "../../Ads";
import MainCalculatorsList from "./MainCalculators";
const unitTranslations = {
  celsius: "درجة مئوية",
  fahrenheit: "فهرنهايت",
  kelvin: "كلفن",
};
const unitSymbols = {
  celsius: "°C",
  fahrenheit: "°F",
  kelvin: "K",
};
const convertTemperature = (amount, fromUnit, toUnit) => {
  if (fromUnit === toUnit) return amount;
  // Convert to Celsius first
  let celsius;
  if (fromUnit === "celsius") {
    celsius = amount;
  } else if (fromUnit === "fahrenheit") {
    celsius = ((amount - 32) * 5) / 9;
  } else if (fromUnit === "kelvin") {
    celsius = amount - 273.15;
  }
  // Convert from Celsius to target unit
  if (toUnit === "celsius") {
    return celsius;
  } else if (toUnit === "fahrenheit") {
    return (celsius * 9) / 5 + 32;
  } else if (toUnit === "kelvin") {
    return celsius + 273.15;
  }
  return amount;
};
function TemperatureConverter() {
  const { fromTemperatureUnit, toTemperatureUnit } = useParams();
  const [amount, setAmount] = useState("");
  const [convertedAmount, setConvertedAmount] = useState(null);
  const translatedFromUnit =
    unitTranslations[fromTemperatureUnit] || fromTemperatureUnit;
  const translatedToUnit =
    unitTranslations[toTemperatureUnit] || toTemperatureUnit;
  const fromSymbol = unitSymbols[fromTemperatureUnit] || "";
  const toSymbol = unitSymbols[toTemperatureUnit] || "";
  const TITLE = `تحويل من ${translatedFromUnit} إلى ${translatedToUnit} - محول درجة الحرارة`;
  const DESCRIPTION = `محول درجة الحرارة الدقيق لتحويل ${translatedFromUnit} إلى ${translatedToUnit}. حوّل بين درجات الحرارة المئوية وفهرنهايت وكلفن بسهولة مع معادلات التحويل وجدول شامل. أداة مجانية وسريعة مع أمثلة عملية.`;
  const KEYWORDS = `تحويل ${translatedFromUnit} إلى ${translatedToUnit}, ${translatedFromUnit} ${translatedToUnit}, محول درجة الحرارة, ${fromTemperatureUnit} to ${toTemperatureUnit}, حاسبة الحرارة, تحويل سيليزيوس فهرنهايت, كلفن`;
  const OG_URL = `https://do-calculate.com/calculator/ar/unit_converter/temperature/${fromTemperatureUnit}/${toTemperatureUnit}/`;
  useEffect(() => {
    if (amount && !isNaN(amount)) {
      const result = convertTemperature(
        Number(amount),
        fromTemperatureUnit,
        toTemperatureUnit
      );
      setConvertedAmount(result);
    } else {
      setConvertedAmount(null);
    }
  }, [amount, fromTemperatureUnit, toTemperatureUnit]);
  const handleAmountChange = (e) => {
    setAmount(e.target.value);
  };
  const handleReset = () => {
    setAmount("");
    setConvertedAmount(null);
  };
  const amounts = [-273.15, -100, -50, -20, 0, 20, 37, 50, 100, 200, 500, 1000];
  const conversionTable = amounts.map((a) => ({
    fromAmount: a,
    toAmount: convertTemperature(a, fromTemperatureUnit, toTemperatureUnit),
  }));
  // Format numbers
  const formatNumber = (num) => {
    if (num === null || num === undefined || isNaN(num)) {
      return "0";
    }
    return num.toLocaleString(undefined, {
      maximumFractionDigits: 2,
      minimumFractionDigits: 2,
    });
  };
  // Generate Schema.org structured data
  const generateSchemaData = () => {
    const breadcrumbSchema = {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        {
          "@type": "ListItem",
          position: 1,
          name: "الصفحة الرئيسية",
          item: "https://do-calculate.com/",
        },
        {
          "@type": "ListItem",
          position: 2,
          name: "محولات الوحدات",
          item: "https://do-calculate.com/ar/unit_converter/",
        },
        {
          "@type": "ListItem",
          position: 3,
          name: "تحويل درجة الحرارة",
          item: "https://do-calculate.com/ar/unit_converter/temperature/",
        },
        {
          "@type": "ListItem",
          position: 4,
          name: `${translatedFromUnit} إلى ${translatedToUnit}`,
          item: OG_URL,
        },
      ],
    };
    const calculatorSchema = {
      "@context": "https://schema.org",
      "@type": "WebApplication",
      name: `محول ${translatedFromUnit} إلى ${translatedToUnit}`,
      description: DESCRIPTION,
      url: OG_URL,
      applicationCategory: "UtilityApplication",
      operatingSystem: "Any",
      offers: {
        "@type": "Offer",
        price: "0",
        priceCurrency: "USD",
      },
    };
    return [breadcrumbSchema, calculatorSchema];
  };
  // Get conversion formula
  const getConversionFormula = () => {
    if (fromTemperatureUnit === "celsius" && toTemperatureUnit === "fahrenheit") {
      return "(°C × 9/5) + 32 = °F";
    } else if (
      fromTemperatureUnit === "fahrenheit" &&
      toTemperatureUnit === "celsius"
    ) {
      return "(°F - 32) × 5/9 = °C";
    } else if (fromTemperatureUnit === "celsius" && toTemperatureUnit === "kelvin") {
      return "°C + 273.15 = K";
    } else if (fromTemperatureUnit === "kelvin" && toTemperatureUnit === "celsius") {
      return "K - 273.15 = °C";
    } else if (
      fromTemperatureUnit === "fahrenheit" &&
      toTemperatureUnit === "kelvin"
    ) {
      return "(°F - 32) × 5/9 + 273.15 = K";
    } else if (
      fromTemperatureUnit === "kelvin" &&
      toTemperatureUnit === "fahrenheit"
    ) {
      return "(K - 273.15) × 9/5 + 32 = °F";
    }
    return "القيمة نفسها";
  };
  return (
    <div className="rtl" style={{ direction: "rtl" }}>
      <SharedHelmet
        TITLE={TITLE}
        DESCRIPTION={DESCRIPTION}
        KEYWORDS={KEYWORDS}
        OG_URL={OG_URL}
        SCHEMA={generateSchemaData()}
      />
      <div className="row">
        <div className="col-12 col-md-8">
          <FirstAds />
        </div>
      </div>
      <div className="row">
        <div className="col-12 col-md-8">
          <br />
          <div
            className="bg-success text-white text-center"
            style={{ padding: "15px", borderRadius: "12px" }}
          >
            <h1 style={{ fontSize: "22px", margin: 0 }}>{TITLE}</h1>
          </div>
          <div className="border border-light mt-3 p-3 bg-white shadow-sm">
            <div className="mb-4">
              <p style={{ fontSize: "16px", lineHeight: "1.8" }}>
                <strong>
                  محول {translatedFromUnit} إلى {translatedToUnit}
                </strong>{" "}
                هو أداة دقيقة وسريعة لتحويل درجات الحرارة. حوّل{" "}
                <strong>{translatedFromUnit}</strong> إلى{" "}
                <strong>{translatedToUnit}</strong> بكل سهولة مع معادلة التحويل
                وجدول شامل للقيم الشائعة.
              </p>
            </div>
            <hr />
            <MobileUnderCalAds />
            <hr />
            <div className="row">
             <div className="col-sm-12 col-md-6 calculatorStyle">
                <div className="p-3 mb-3 bg-success text-white text-center rounded">
                  <h2 style={{ fontSize: "18px", margin: 0 }}>
                    {translatedFromUnit} → {translatedToUnit}
                  </h2>
                </div>
                <div
                  className="alert alert-info"
                  style={{ fontSize: "15px", lineHeight: "1.8" }}
                >
                  <strong>معادلة التحويل:</strong>
                  <br />
                  {getConversionFormula()}
                  <br/>                  <strong>مثال:</strong> 1 {translatedFromUnit} ={" "}
                  {formatNumber(
                    convertTemperature(1, fromTemperatureUnit, toTemperatureUnit)
                  )}{" "}
                  {translatedToUnit}
                </div>
                <form onSubmit={(e) => e.preventDefault()}>
                  <div className="form-group mb-3">
                    <label  htmlFor="amount">
                      <strong>أدخل درجة الحرارة بالـ {translatedFromUnit}:</strong>
                    </label>
                    <input
                      id="amount"
                      type="number"
                      className="form-control"
                      value={amount}
                      onChange={handleAmountChange}
                      placeholder={`مثال: 25`}
                      step="any"
                      style={{ fontSize: "16px", padding: "10px" }}
                    />
                  </div>
                  <hr />
                  <div className="d-flex gap-2 flex-wrap">
                    <button
                      type="button"
                      className="btn btn-secondary"
                      onClick={handleReset}
                      style={{ fontSize: "16px", padding: "10px 20px" }}
                    >
                      امسح
                    </button>
                    <Link
                      to={`/calculator/ar/unit_converter/temperature/${toTemperatureUnit}/${fromTemperatureUnit}/`}
                    >
                      <button
                        className="btn btn-outline-success"
                        style={{ fontSize: "15px", padding: "10px 15px" }}
                      >
                        🔄 عكس التحويل ({translatedToUnit} → {translatedFromUnit}
                        )
                      </button>
                    </Link>
                  </div>
                </form>
              </div>
              <div className="col-sm-12 col-md-4">
                {convertedAmount !== null && amount && (
                  <div className="calculatorStyle">
                    <div className="p-3 mb-3 bg-success text-white text-center rounded">
                      <h3 style={{ fontSize: "18px", margin: 0 }}>النتيجة</h3>
                    </div>
                    <div
                      className="alert alert-success"
                      style={{ fontSize: "16px", lineHeight: "1.8" }}
                    >
                      <div
                        style={{
                          textAlign: "center",
                          padding: "20px",
                          backgroundColor: "#d4edda",
                          borderRadius: "8px",
                          marginBottom: "15px",
                        }}
                      >
                        <div
                          style={{
                            fontSize: "18px",
                            marginBottom: "10px",
                            color: "#155724",
                          }}
                        >
                          <strong>
                            {Number(amount).toLocaleString()} {fromSymbol}
                          </strong>
                        </div>
                        <div
                          style={{
                            fontSize: "32px",
                            fontWeight: "bold",
                            color: "#155724",
                          }}
                        >
                          = {formatNumber(convertedAmount)} {toSymbol}
                        </div>
                      </div>
                      <div
                        style={{
                          fontSize: "14px",
                          marginTop: "15px",
                          backgroundColor: "#fff",
                          padding: "15px",
                          borderRadius: "8px",
                        }}
                      >
                        <strong>المعادلة المستخدمة:</strong>
                        <div style={{ marginTop: "10px", lineHeight: "1.8" }}>
                          {getConversionFormula()}
                        </div>
                      </div>
                    </div>
                  </div>
                )}
                {!amount && (
                  <div className="alert alert-warning" role="alert">
                    <strong>ملاحظة:</strong> أدخل درجة الحرارة لرؤية النتيجة
                  </div>
                )}
              </div>
            </div>
            <hr />
            <div>
              <h3 style={{ fontSize: "18px", marginBottom: "15px" }}>
                جدول التحويل من {translatedFromUnit} إلى {translatedToUnit}
              </h3>
              <p
                className="alert alert-success"
                style={{ fontSize: "15px", lineHeight: "1.8" }}
              >
                الجدول التالي يوضح تحويل درجات حرارة شائعة من{" "}
                <strong>{translatedFromUnit}</strong> إلى{" "}
                <strong>{translatedToUnit}</strong>:
              </p>
              <div style={{ overflowX: "auto" }}>
                <table className="table table-bordered table-hover">
                  <thead>
                    <tr style={{ backgroundColor: "#e7f3fe" }}>
                      <th style={{ textAlign: "center", padding: "12px" }}>
                        {translatedFromUnit} ({fromSymbol})
                      </th>
                      <th style={{ textAlign: "center", padding: "12px" }}>
                        {translatedToUnit} ({toSymbol})
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {conversionTable.map((entry, index) => (
                      <tr key={index}>
                        <td style={{ textAlign: "center", padding: "10px" }}>
                          {formatNumber(entry.fromAmount)}
                        </td>
                        <td style={{ textAlign: "center", padding: "10px" }}>
                          {formatNumber(entry.toAmount)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
            <hr />
            <div
              className="alert alert-info"
              style={{ fontSize: "15px", lineHeight: "1.8" }}
            >
              💡 <strong>نصيحة:</strong> يمكنك أيضاً{" "}
              <Link to={`/calculator/ar/unit_converter/temperature/`}>
                استعراض جميع تحويلات درجة الحرارة المتاحة
              </Link>{" "}
              أو{" "}
              <Link
                to={`/calculator/ar/unit_converter/temperature/${toTemperatureUnit}/${fromTemperatureUnit}/`}
              >
                عكس التحويل من {translatedToUnit} إلى {translatedFromUnit}
              </Link>
              .
            </div>
            <hr />
            <div className="row">
              <RelatedLinks />
            </div>
          </div>
        </div>
        <div className="col-sm-12 col-md-4">
          <SecondAds />
          <br />
          <MainCalculatorsList />
        </div>
      </div>
      {/* Educational Content */}
      <div className="row" style={{ marginTop: "20px" }}>
        <div className="col-12 col-md-8">
          <div className="info-sections-container">
            <div className="info-section">
              <div className="info-section-header">
                <strong>
                  كيف تحول من {translatedFromUnit} إلى {translatedToUnit}؟
                </strong>
              </div>
              <div className="info-section-body">
                <p style={{ lineHeight: "1.8" }}>
                  تحويل <strong>{translatedFromUnit}</strong> إلى{" "}
                  <strong>{translatedToUnit}</strong> يتطلب استخدام معادلة تحويل
                  محددة لكل وحدة.
                </p>
                <h3 style={{ fontSize: "17px", marginTop: "15px" }}>
                  معادلة التحويل:
                </h3>
                <div
                  className="alert alert-success"
                  style={{ lineHeight: "1.8", backgroundColor: "#d4edda" }}
                >
                  <strong>المعادلة:</strong>
                  <br />
                  {getConversionFormula()}
                  <br/>                  {fromTemperatureUnit === "celsius" &&
                    toTemperatureUnit === "fahrenheit" && (
                      <>
                        <strong>الخطوات:</strong>
                        <ol>
                          <li>اضرب درجة الحرارة بالسيليزيوس في 9</li>
                          <li>اقسم الناتج على 5</li>
                          <li>أضف 32 إلى النتيجة</li>
                        </ol>
                      </>
                    )}
                  {fromTemperatureUnit === "fahrenheit" &&
                    toTemperatureUnit === "celsius" && (
                      <>
                        <strong>الخطوات:</strong>
                        <ol>
                          <li>اطرح 32 من درجة الحرارة بالفهرنهايت</li>
                          <li>اضرب الناتج في 5</li>
                          <li>اقسم النتيجة على 9</li>
                        </ol>
                      </>
                    )}
                  {fromTemperatureUnit === "celsius" &&
                    toTemperatureUnit === "kelvin" && (
                      <>
                        <strong>الخطوات:</strong>
                        <ol>
                          <li>أضف 273.15 إلى درجة الحرارة بالسيليزيوس</li>
                        </ol>
                      </>
                    )}
                  {fromTemperatureUnit === "kelvin" &&
                    toTemperatureUnit === "celsius" && (
                      <>
                        <strong>الخطوات:</strong>
                        <ol>
                          <li>اطرح 273.15 من درجة الحرارة بالكلفن</li>
                        </ol>
                      </>
                    )}
                </div>
                <h3 style={{ fontSize: "17px", marginTop: "15px" }}>
                  أمثلة عملية:
                </h3>
                <div
                  className="alert alert-info"
                  style={{ lineHeight: "1.8", backgroundColor: "#d1ecf1" }}
                >
                  <strong>مثال 1:</strong> تحويل 0 {translatedFromUnit} إلى{" "}
                  {translatedToUnit}
                  <br />
                  النتيجة ={" "}
                  {formatNumber(
                    convertTemperature(0, fromTemperatureUnit, toTemperatureUnit)
                  )}{" "}
                  {translatedToUnit}
                  <br/>                  <strong>مثال 2:</strong> تحويل 100 {translatedFromUnit} إلى{" "}
                  {translatedToUnit}
                  <br />
                  النتيجة ={" "}
                  {formatNumber(
                    convertTemperature(100, fromTemperatureUnit, toTemperatureUnit)
                  )}{" "}
                  {translatedToUnit}
                  <br/>                  <strong>مثال 3:</strong> تحويل 37 {translatedFromUnit} (درجة
                  حرارة الجسم) إلى {translatedToUnit}
                  <br />
                  النتيجة ={" "}
                  {formatNumber(
                    convertTemperature(37, fromTemperatureUnit, toTemperatureUnit)
                  )}{" "}
                  {translatedToUnit}
                </div>
                <h3 style={{ fontSize: "17px", marginTop: "15px" }}>
                  نقاط مرجعية مهمة:
                </h3>
                <ul style={{ lineHeight: "1.8" }}>
                  <li>
                    <strong>نقطة تجمد الماء:</strong> 0°C = 32°F = 273.15 K
                  </li>
                  <li>
                    <strong>نقطة غليان الماء:</strong> 100°C = 212°F = 373.15 K
                  </li>
                  <li>
                    <strong>درجة حرارة الجسم:</strong> 37°C = 98.6°F = 310.15 K
                  </li>
                  <li>
                    <strong>الصفر المطلق:</strong> -273.15°C = -459.67°F = 0 K
                  </li>
                </ul>
              </div>
            </div>
            <div className="info-section">
              <div className="info-section-header">
                <strong>
                  معلومات عن وحدات درجة الحرارة ({translatedFromUnit} و
                  {translatedToUnit})
                </strong>
              </div>
              <div className="info-section-body">
                <h3 style={{ fontSize: "17px", marginBottom: "10px" }}>
                  <strong>درجة مئوية (سيليزيوس - °C):</strong>
                </h3>
                <div
                  className="alert alert-success"
                  style={{ lineHeight: "1.8", backgroundColor: "#d4edda" }}
                >
                  <ul>
                    <li>
                      <strong>الوحدة الأكثر استخداماً</strong> في معظم دول
                      العالم
                    </li>
                    <li>
                      سميت على اسم العالم السويدي أندرس سيليزيوس (1742)
                    </li>
                    <li>
                      <strong>نقطة التجمد:</strong> 0°C
                    </li>
                    <li>
                      <strong>نقطة الغليان:</strong> 100°C (عند الضغط الجوي
                      القياسي)
                    </li>
                    <li>تستخدم في الطقس، الطب، الطبخ، العلوم</li>
                  </ul>
                </div>
                <h3
                  style={{
                    fontSize: "17px",
                    marginTop: "15px",
                    marginBottom: "10px",
                  }}
                >
                  <strong>فهرنهايت (°F):</strong>
                </h3>
                <div
                  className="alert alert-info"
                  style={{ lineHeight: "1.8", backgroundColor: "#d1ecf1" }}
                >
                  <ul>
                    <li>
                      تستخدم بشكل أساسي في <strong>الولايات المتحدة</strong>{" "}
                      وبعض دول الكاريبي
                    </li>
                    <li>سميت على اسم العالم الألماني دانييل فهرنهايت (1724)</li>
                    <li>
                      <strong>نقطة التجمد:</strong> 32°F
                    </li>
                    <li>
                      <strong>نقطة الغليان:</strong> 212°F
                    </li>
                    <li>توفر دقة أعلى للقياسات اليومية (أرقام أكبر)</li>
                  </ul>
                </div>
                <h3
                  style={{
                    fontSize: "17px",
                    marginTop: "15px",
                    marginBottom: "10px",
                  }}
                >
                  <strong>كلفن (K):</strong>
                </h3>
                <div
                  className="alert alert-warning"
                  style={{ lineHeight: "1.8", backgroundColor: "#fff3cd" }}
                >
                  <ul>
                    <li>
                      <strong>وحدة النظام الدولي</strong> لدرجة الحرارة
                      الديناميكية الحرارية
                    </li>
                    <li>
                      سميت على اسم العالم البريطاني ويليام طومسون (اللورد كلفن)
                    </li>
                    <li>
                      <strong>لا توجد درجات سالبة</strong> - تبدأ من الصفر
                      المطلق
                    </li>
                    <li>
                      <strong>الصفر المطلق:</strong> 0 K (-273.15°C)
                    </li>
                    <li>
                      <strong>نقطة التجمد:</strong> 273.15 K
                    </li>
                    <li>
                      <strong>نقطة الغليان:</strong> 373.15 K
                    </li>
                    <li>تستخدم في الفيزياء والكيمياء والأبحاث العلمية</li>
                    <li>
                      لا تستخدم رمز الدرجة (°) - فقط K وليس °K
                    </li>
                  </ul>
                </div>
                <h3 style={{ fontSize: "17px", marginTop: "15px" }}>
                  الفروقات الرئيسية:
                </h3>
                <table
                  className="table table-bordered"
                  style={{ width: "100%", marginTop: "10px" }}
                >
                  <thead>
                    <tr style={{ backgroundColor: "#e7f3fe" }}>
                      <th style={{ textAlign: "center", padding: "12px" }}>
                        النقطة
                      </th>
                      <th style={{ textAlign: "center", padding: "12px" }}>
                        سيليزيوس
                      </th>
                      <th style={{ textAlign: "center", padding: "12px" }}>
                        فهرنهايت
                      </th>
                      <th style={{ textAlign: "center", padding: "12px" }}>
                        كلفن
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td style={{ padding: "10px" }}>الصفر المطلق</td>
                      <td style={{ textAlign: "center", padding: "10px" }}>
                        -273.15°C
                      </td>
                      <td style={{ textAlign: "center", padding: "10px" }}>
                        -459.67°F
                      </td>
                      <td style={{ textAlign: "center", padding: "10px" }}>
                        0 K
                      </td>
                    </tr>
                    <tr>
                      <td style={{ padding: "10px" }}>تجمد الماء</td>
                      <td style={{ textAlign: "center", padding: "10px" }}>
                        0°C
                      </td>
                      <td style={{ textAlign: "center", padding: "10px" }}>
                        32°F
                      </td>
                      <td style={{ textAlign: "center", padding: "10px" }}>
                        273.15 K
                      </td>
                    </tr>
                    <tr>
                      <td style={{ padding: "10px" }}>حرارة الجسم</td>
                      <td style={{ textAlign: "center", padding: "10px" }}>
                        37°C
                      </td>
                      <td style={{ textAlign: "center", padding: "10px" }}>
                        98.6°F
                      </td>
                      <td style={{ textAlign: "center", padding: "10px" }}>
                        310.15 K
                      </td>
                    </tr>
                    <tr>
                      <td style={{ padding: "10px" }}>غليان الماء</td>
                      <td style={{ textAlign: "center", padding: "10px" }}>
                        100°C
                      </td>
                      <td style={{ textAlign: "center", padding: "10px" }}>
                        212°F
                      </td>
                      <td style={{ textAlign: "center", padding: "10px" }}>
                        373.15 K
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
            <div className="info-section">
              <div className="info-section-header">
                <strong>استخدامات عملية لتحويل درجة الحرارة</strong>
              </div>
              <div className="info-section-body">
                <h3 style={{ fontSize: "17px", marginBottom: "15px" }}>
                  متى تحتاج لتحويل درجات الحرارة؟
                </h3>
                <div
                  className="alert alert-success"
                  style={{ lineHeight: "1.8", backgroundColor: "#d4edda" }}
                >
                  <h4 style={{ fontSize: "16px" }}>
                    <strong>في الحياة اليومية:</strong>
                  </h4>
                  <ul>
                    <li>
                      <strong>السفر:</strong> فهم توقعات الطقس في دول تستخدم
                      وحدات مختلفة
                    </li>
                    <li>
                      <strong>الطبخ:</strong> اتباع وصفات من مصادر عالمية
                      (حرارة الفرن)
                    </li>
                    <li>
                      <strong>الصحة:</strong> قراءة قياسات الحرارة من أجهزة
                      مختلفة
                    </li>
                    <li>
                      <strong>التسوق:</strong> فهم مواصفات الأجهزة الكهربائية
                    </li>
                  </ul>
                </div>
                <div
                  className="alert alert-info"
                  style={{ lineHeight: "1.8", backgroundColor: "#d1ecf1" }}
                >
                  <h4 style={{ fontSize: "16px" }}>
                    <strong>في المجالات المهنية:</strong>
                  </h4>
                  <ul>
                    <li>
                      <strong>الطب:</strong> تحويل قياسات حرارة المرضى بين
                      الوحدات
                    </li>
                    <li>
                      <strong>الطبخ المهني:</strong> ضبط حرارة الأفران
                      والمعدات
                    </li>
                    <li>
                      <strong>الهندسة:</strong> حسابات حرارية في التصميم
                      والتصنيع
                    </li>
                    <li>
                      <strong>البحث العلمي:</strong> توحيد القياسات في
                      التجارب
                    </li>
                    <li>
                      <strong>الأرصاد الجوية:</strong> نشر التوقعات بوحدات
                      مختلفة
                    </li>
                    <li>
                      <strong>التكييف والتبريد:</strong> ضبط أنظمة التحكم في
                      الحرارة
                    </li>
                  </ul>
                </div>
                <div
                  className="alert alert-warning"
                  style={{ lineHeight: "1.8", backgroundColor: "#fff3cd" }}
                >
                  <h4 style={{ fontSize: "16px" }}>
                    <strong>نصائح مهمة:</strong>
                  </h4>
                  <ul>
                    <li>
                      <strong>للحفظ السريع:</strong> 0°C = 32°F و 100°C =
                      212°F
                    </li>
                    <li>
                      <strong>قاعدة تقريبية:</strong> لتحويل C إلى F: اضرب
                      في 2 وأضف 30 (تقريبي)
                    </li>
                    <li>
                      <strong>لتحويل F إلى C:</strong> اطرح 30 واقسم على 2
                      (تقريبي)
                    </li>
                    <li>
                      <strong>في الطبخ:</strong> استخدم المحول للدقة في ضبط
                      حرارة الفرن
                    </li>
                    <li>
                      <strong>في الصحة:</strong> تعرّف على درجة الحرارة
                      الطبيعية بكلا الوحدتين
                    </li>
                    <li>
                      <strong>كلفن للعلوم:</strong> دائماً موجبة، لا تستخدم
                      رمز الدرجة
                    </li>
                  </ul>
                </div>
                <h3 style={{ fontSize: "17px", marginTop: "15px" }}>
                  درجات حرارة شائعة ومعانيها:
                </h3>
                <table
                  className="table table-bordered"
                  style={{ width: "100%", marginTop: "10px" }}
                >
                  <thead>
                    <tr style={{ backgroundColor: "#e7f3fe" }}>
                      <th style={{ padding: "12px" }}>الوصف</th>
                      <th style={{ textAlign: "center", padding: "12px" }}>
                        °C
                      </th>
                      <th style={{ textAlign: "center", padding: "12px" }}>
                        °F
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td style={{ padding: "10px" }}>
                        برودة شديدة (تجمد قوي)
                      </td>
                      <td style={{ textAlign: "center", padding: "10px" }}>
                        -20
                      </td>
                      <td style={{ textAlign: "center", padding: "10px" }}>
                        -4
                      </td>
                    </tr>
                    <tr>
                      <td style={{ padding: "10px" }}>تجمد الماء</td>
                      <td style={{ textAlign: "center", padding: "10px" }}>
                        0
                      </td>
                      <td style={{ textAlign: "center", padding: "10px" }}>
                        32
                      </td>
                    </tr>
                    <tr>
                      <td style={{ padding: "10px" }}>طقس بارد</td>
                      <td style={{ textAlign: "center", padding: "10px" }}>
                        10
                      </td>
                      <td style={{ textAlign: "center", padding: "10px" }}>
                        50
                      </td>
                    </tr>
                    <tr>
                      <td style={{ padding: "10px" }}>
                        درجة حرارة الغرفة
                      </td>
                      <td style={{ textAlign: "center", padding: "10px" }}>
                        20-25
                      </td>
                      <td style={{ textAlign: "center", padding: "10px" }}>
                        68-77
                      </td>
                    </tr>
                    <tr>
                      <td style={{ padding: "10px" }}>
                        طقس حار
                      </td>
                      <td style={{ textAlign: "center", padding: "10px" }}>
                        35
                      </td>
                      <td style={{ textAlign: "center", padding: "10px" }}>
                        95
                      </td>
                    </tr>
                    <tr>
                      <td style={{ padding: "10px" }}>
                        درجة حرارة الجسم الطبيعية
                      </td>
                      <td style={{ textAlign: "center", padding: "10px" }}>
                        37
                      </td>
                      <td style={{ textAlign: "center", padding: "10px" }}>
                        98.6
                      </td>
                    </tr>
                    <tr>
                      <td style={{ padding: "10px" }}>
                        حمى خفيفة
                      </td>
                      <td style={{ textAlign: "center", padding: "10px" }}>
                        38
                      </td>
                      <td style={{ textAlign: "center", padding: "10px" }}>
                        100.4
                      </td>
                    </tr>
                    <tr>
                      <td style={{ padding: "10px" }}>
                        غليان الماء
                      </td>
                      <td style={{ textAlign: "center", padding: "10px" }}>
                        100
                      </td>
                      <td style={{ textAlign: "center", padding: "10px" }}>
                        212
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
         </div>
</div>
  );
}
export default TemperatureConverter;